import { Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import themeColors from "../theme";

/**
 * First-time orientation overlay for the Dashboard.
 *
 * Renders a "spotlight" cutout over the currently highlighted tile (the rest of
 * the screen is dimmed) plus a popup card describing that tile. Stepping through
 * advances tile-by-tile; the last step finishes the tour.
 *
 * Positioning is driven by `targetRect` (a DOMRect from the active tile) which
 * the parent recomputes on step change, scroll, and resize.
 */
export default function DashboardTour({
    steps,
    stepIndex,
    targetRect,
    onNext,
    onClose,
}) {
    const { teal } = themeColors;

    if (!targetRect) return null;

    const step = steps[stepIndex];
    const isLast = stepIndex === steps.length - 1;

    // Pad the spotlight slightly so the tile breathes inside the cutout.
    const pad = 8;
    const rect = {
        top: targetRect.top - pad,
        left: targetRect.left - pad,
        width: targetRect.width + pad * 2,
        height: targetRect.height + pad * 2,
    };

    // Decide whether the card sits below or above the tile based on free space.
    const margin = 16;
    const cardWidth = 380;
    const spaceBelow = window.innerHeight - targetRect.bottom;
    const placeBelow = spaceBelow > 260;

    const cardLeft = Math.min(
        Math.max(targetRect.left + targetRect.width / 2, cardWidth / 2 + 12),
        window.innerWidth - cardWidth / 2 - 12
    );
    const cardTop = placeBelow ? targetRect.bottom + margin : targetRect.top - margin;
    const cardTransform = placeBelow
        ? "translateX(-50%)"
        : "translate(-50%, -100%)";

    return (
        <>
            {/* Spotlight: dims everything except the active tile via a huge
                box-shadow spread, leaving the real tile visible underneath. */}
            <Box
                sx={{
                    position: "fixed",
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                    borderRadius: "12px",
                    border: `2px solid ${teal}`,
                    backgroundColor: "rgba(0, 127, 126, 0.12)",
                    boxShadow: `0 0 0 9999px rgba(0,0,0,0.55), 0 0 24px rgba(0,127,126,0.6)`,
                    pointerEvents: "none",
                    zIndex: 1300,
                    transition: "all 0.25s ease",
                }}
            />

            {/* Popup card */}
            <Box
                sx={{
                    position: "fixed",
                    top: cardTop,
                    left: cardLeft,
                    transform: cardTransform,
                    width: cardWidth,
                    maxWidth: "calc(100vw - 32px)",
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                    p: 3,
                    zIndex: 1400,
                    textAlign: "center",
                }}
            >
                <IconButton
                    aria-label="Close tour"
                    onClick={onClose}
                    size="small"
                    sx={{ position: "absolute", top: 6, right: 6, color: "#9E9E9E" }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <Typography variant="body1" color="#2C3E50" sx={{ mb: 2, mt: 1 }}>
                    {step.text}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                    <Typography variant="caption" color="#9E9E9E">
                        {stepIndex + 1} of {steps.length}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={onNext}
                        sx={{
                            backgroundColor: teal,
                            textTransform: "none",
                            borderRadius: "8px",
                            px: 3,
                            "&:hover": { backgroundColor: "#005F5E" },
                        }}
                    >
                        {isLast ? "Done" : "OK, Got It"}
                    </Button>
                </Box>
            </Box>
        </>
    );
}
